import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Camera, Upload, Loader2, X, Check, AlertCircle, ArrowLeft } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cf82e0fe-468c-4a43-97a9-405e8da70dca/artifacts/znleb4a2_260207%20Logo%20Galeries%20Yess5zw.png";

// Category-based starting bids
const STARTING_BIDS = {
    coat: 45,
    dress: 35,
    top: 20,
    pants: 25,
    shoes: 30,
    accessories: 15,
    other: 20,
};

const MAX_ITEMS_PER_USER = 5;

const AddItemPage = () => {
    const { t, language } = useLanguage();
    const { api } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const fileInputRef = useRef(null);
    
    const editItemId = searchParams.get('edit');
    const [isEditMode, setIsEditMode] = useState(false);
    
    const [currentEvent, setCurrentEvent] = useState(null);
    const [userItemCount, setUserItemCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [showPublishConfirm, setShowPublishConfirm] = useState(false);
    
    const [itemData, setItemData] = useState({
        title: '',
        category: '',
        size_text: '',
        materials_text: '',
        care_text: '',
        brand: '',
        price: '',
        photos_original: [],
    });
    
    const [previewImages, setPreviewImages] = useState([]);
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

    const categories = ['coat', 'dress', 'top', 'pants', 'shoes', 'accessories', 'other'];

    // PWA scroll fix - scroll to top on mount
    useEffect(() => {
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
        
        const timer = setTimeout(() => {
            window.scrollTo(0, 0);
        }, 100);
        
        return () => clearTimeout(timer);
    }, []);

    // Load existing item if in edit mode
    useEffect(() => {
        if (editItemId) {
            fetchItemForEdit(editItemId);
        }
    }, [editItemId]);

    const fetchItemForEdit = async (itemId) => {
        setLoading(true);
        try {
            const response = await api().get(`/items/${itemId}`);
            const item = response.data;
            
            setItemData({
                title: item.title || '',
                category: item.category || '',
                size_text: item.size_text || '',
                materials_text: item.materials_text || '',
                care_text: item.care_text || '',
                brand: item.brand || '',
                price: item.price?.toString() || '',
                photos_original: item.photos_original || [],
            });
            
            // Set preview images from existing photos
            const existingPhotos = item.photo_processed 
                ? [item.photo_processed] 
                : (item.photos_original || []);
            setPreviewImages(existingPhotos);
            
            setIsEditMode(true);
            setDisclaimerAccepted(true); // Already accepted when first published
        } catch (error) {
            console.error('Failed to fetch item for edit:', error);
            toast.error('Could not load item');
            navigate('/my-yess');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCurrentEvent();
        fetchUserItemCount();
    }, []);

    // Update starting bid when category changes
    useEffect(() => {
        if (itemData.category && STARTING_BIDS[itemData.category]) {
            setItemData(prev => ({
                ...prev,
                price: STARTING_BIDS[itemData.category].toString()
            }));
        }
    }, [itemData.category]);

    const fetchCurrentEvent = async () => {
        try {
            const response = await api().get('/events/current');
            setCurrentEvent(response.data);
        } catch (error) {
            console.error('No current event:', error);
        }
    };

    const fetchUserItemCount = async () => {
        try {
            const response = await api().get('/items/my/items');
            setUserItemCount(response.data.length);
        } catch (error) {
            console.error('Failed to fetch user items:', error);
        }
    };

    const MAX_PHOTOS_PER_ITEM = 3;

    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Check if adding these would exceed the limit
        const remainingSlots = MAX_PHOTOS_PER_ITEM - previewImages.length;
        if (remainingSlots <= 0) {
            toast.error(`Maximum ${MAX_PHOTOS_PER_ITEM} photos per item`);
            return;
        }

        // Only process up to remaining slots
        const filesToProcess = files.slice(0, remainingSlots);
        if (files.length > remainingSlots) {
            toast.info(`Only adding ${remainingSlots} more photo(s) (max ${MAX_PHOTOS_PER_ITEM})`);
        }

        setProcessing(true);
        
        for (const file of filesToProcess) {
            const reader = new FileReader();
            reader.onload = async (event) => {
                const base64Image = event.target.result;
                const base64Data = base64Image.split(',')[1];
                
                // First add the original image as preview
                setPreviewImages(prev => [...prev, base64Image]);
                
                // Just use original image - no background processing
                setItemData(prev => ({
                    ...prev,
                    photos_original: [...prev.photos_original, base64Image]
                }));

                // Run AI recognition on first image
                if (currentEvent && itemData.photos_original.length === 0) {
                    try {
                        const response = await api().post('/ai/recognize-item', {
                            image_base64: base64Data,
                            event_id: currentEvent.id,
                        });
                        
                        if (response.data) {
                            const category = response.data.category || '';
                            setItemData(prev => ({
                                ...prev,
                                title: response.data.suggested_title || prev.title,
                                category: category,
                                detected_type: response.data.detected_type,
                                price: STARTING_BIDS[category]?.toString() || prev.price,
                            }));
                            toast.success('Item recognized! Review the details.');
                        }
                    } catch (error) {
                        console.error('Recognition failed:', error);
                    }
                }
            };
            reader.readAsDataURL(file);
        }
        
        setProcessing(false);
    };

    const handleRemoveImage = (index) => {
        setPreviewImages(prev => prev.filter((_, i) => i !== index));
        setItemData(prev => ({
            ...prev,
            photos_original: prev.photos_original.filter((_, i) => i !== index),
        }));
    };

    // Validate form and return errors
    const validateForm = () => {
        const errors = {};
        
        if (itemData.photos_original.length === 0) {
            errors.photos = language === 'nl' ? 'Voeg minimaal 1 foto toe' : 'Add at least 1 photo';
        }
        if (!itemData.title || itemData.title.trim() === '') {
            errors.title = language === 'nl' ? 'Titel is verplicht' : 'Title is required';
        }
        if (!itemData.category) {
            errors.category = language === 'nl' ? 'Categorie is verplicht' : 'Category is required';
        }
        if (!disclaimerAccepted) {
            errors.disclaimer = language === 'nl' ? 'Accepteer de disclaimer' : 'Accept the disclaimer';
        }
        
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Clear previous errors
        setValidationErrors({});
        
        if (!currentEvent) {
            toast.error('No active auction to add items to');
            return;
        }

        if (userItemCount >= MAX_ITEMS_PER_USER) {
            toast.error(t('maxItemsReached'));
            return;
        }
        
        // Validate all required fields
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            // Show toast with first error
            const firstError = Object.values(errors)[0];
            toast.error(firstError);
            return;
        }
        
        if (!itemData.price || parseFloat(itemData.price) <= 0) {
            toast.error('Please set a starting bid');
            return;
        }

        // Show confirmation dialog
        setShowPublishConfirm(true);
    };

    const handleConfirmPublish = async () => {
        setShowPublishConfirm(false);
        setLoading(true);
        
        try {
            const payload = {
                event_id: currentEvent?.id,
                title: itemData.title,
                category: itemData.category,
                detected_type: itemData.detected_type,
                size_text: itemData.size_text,
                materials_text: itemData.materials_text,
                care_text: itemData.care_text,
                brand: itemData.brand,
                photos_original: itemData.photos_original,
                price: parseFloat(itemData.price),
                listing_type: 'sell', // Always auction
                status: 'pending', // Will be visible in gallery
            };
            
            if (isEditMode && editItemId) {
                await api().put(`/items/${editItemId}`, payload);
                toast.success(language === 'nl' ? 'Item gepubliceerd!' : 'Item published!');
            } else {
                await api().post('/items/', payload);
                toast.success(language === 'nl' ? 'Item gepubliceerd!' : 'Item published!');
            }
            navigate('/my-yess');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to publish item');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveDraft = async () => {
        setLoading(true);
        try {
            const payload = {
                title: itemData.title,
                category: itemData.category || 'other',
                size_text: itemData.size_text,
                materials_text: itemData.materials_text,
                care_text: itemData.care_text,
                brand: itemData.brand,
                photos_original: itemData.photos_original,
                price: parseFloat(itemData.price) || 20,
                listing_type: 'sell',
                status: 'draft', // Save as draft
            };
            
            if (isEditMode && editItemId) {
                await api().put(`/items/${editItemId}`, payload);
            } else {
                await api().post('/items/', payload);
            }
            toast.success(language === 'nl' ? 'Concept opgeslagen!' : 'Draft saved!');
            navigate('/my-yess');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save draft');
        } finally {
            setLoading(false);
        }
    };

    const canAddMore = userItemCount < MAX_ITEMS_PER_USER || isEditMode;

    return (
        <div className="min-h-screen bg-white safe-area-top">
            {/* Fixed Header */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 safe-area-top">
                <div className="flex items-center justify-between px-4 py-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <img 
                        src={LOGO_URL} 
                        alt="Galeries Yess" 
                        className="h-8 w-auto"
                    />
                    <div className="w-9" />
                </div>
            </div>

            <main className="pt-24 pb-32 px-4 max-w-lg mx-auto">
                {/* Page Title */}
                <h1 className="text-2xl font-heading font-light tracking-tight mb-2">
                    {isEditMode ? (language === 'nl' ? 'Item Bewerken' : 'Edit Item') : 'Add Item'}
                </h1>
                
                {/* Item Count - only show when adding new */}
                {!isEditMode && (
                    <p className="text-sm text-gray-400 mb-8">
                        {userItemCount} / {MAX_ITEMS_PER_USER} items donated
                    </p>
                )}
                {isEditMode && <div className="mb-8" />}

                {/* Item Limit Warning */}
                {!canAddMore && (
                    <div className="bg-white border border-amber-200 p-4 mb-6 flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
                        <p className="text-sm text-amber-700">
                            Maximum items reached
                        </p>
                    </div>
                )}

                {!currentEvent ? (
                    <div className="text-center py-16">
                        <p className="text-gray-400 italic">
                            No active auction
                        </p>
                        <Button onClick={() => navigate('/auction')} variant="outline" className="mt-6">
                            View Auctions
                        </Button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* Photo Section */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-medium">
                                    {language === 'nl' ? 'Foto\'s' : 'Photos'} *
                                </h3>
                                <span className="text-xs text-gray-400">
                                    {previewImages.length}/{MAX_PHOTOS_PER_ITEM}
                                </span>
                            </div>
                            
                            {/* Validation error for photos */}
                            {validationErrors.photos && (
                                <div className="flex items-center gap-2 p-3 bg-pink-50 border border-pink-200 rounded-lg">
                                    <AlertCircle className="w-4 h-4 text-pink-500 shrink-0" />
                                    <span className="text-sm text-pink-600">{validationErrors.photos}</span>
                                </div>
                            )}
                            
                            {previewImages.length > 0 && (
                                <div className="grid grid-cols-3 gap-2">
                                    {previewImages.map((img, index) => (
                                        <div key={index} className="relative aspect-square">
                                            <img
                                                src={img}
                                                alt={`Preview ${index + 1}`}
                                                className="w-full h-full object-cover border border-gray-200"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveImage(index)}
                                                className="absolute top-1 right-1 w-6 h-6 bg-black/80 text-white rounded-full flex items-center justify-center"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="w-full h-32 border-dashed border-border/50 bg-white flex flex-col gap-2"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={processing || !canAddMore || previewImages.length >= MAX_PHOTOS_PER_ITEM}
                                        data-testid="upload-photo-button"
                                    >
                                        {processing ? (
                                            <>
                                                <Loader2 className="w-8 h-8 animate-spin" />
                                                <span className="text-sm">{language === 'nl' ? 'Verwerken...' : 'Processing...'}</span>
                                            </>
                                        ) : previewImages.length >= MAX_PHOTOS_PER_ITEM ? (
                                            <>
                                                <Camera className="w-8 h-8 text-gray-300" />
                                                <span className="text-sm text-gray-400">
                                                    {language === 'nl' ? `Maximum ${MAX_PHOTOS_PER_ITEM} foto's` : `Maximum ${MAX_PHOTOS_PER_ITEM} photos`}
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="w-8 h-8" />
                                                <span className="text-sm">{language === 'nl' ? 'Foto Uploaden' : 'Upload Photo'}</span>
                                            </>
                                        )}
                                    </Button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={handleImageUpload}
                                        className="hidden"
                                    />
                                    <p className="text-xs italic text-center" style={{ color: '#F8BBD6' }}>
                                        Camera recognises where possible
                                    </p>
                                </div>

                        {/* Item Details */}
                        <div className="space-y-6">
                            <div>
                                <label className="text-sm font-medium mb-2 block">{t('itemTitle')} *</label>
                                <Input
                                    value={itemData.title}
                                    onChange={(e) => {
                                        setItemData(prev => ({ ...prev, title: e.target.value }));
                                        if (validationErrors.title) {
                                            setValidationErrors(prev => ({ ...prev, title: null }));
                                        }
                                    }}
                                    placeholder="e.g., Vintage Silk Blouse"
                                    className={`input-minimal bg-white ${validationErrors.title ? 'border-pink-400 focus:border-pink-500' : ''}`}
                                    data-testid="item-title-input"
                                />
                                {validationErrors.title && (
                                    <p className="text-sm text-pink-500 mt-1">{validationErrors.title}</p>
                                )}
                            </div>

                            <div>
                                <label className="text-sm font-medium mb-2 block">{t('itemCategory')} *</label>
                                <Select
                                    value={itemData.category}
                                    onValueChange={(v) => {
                                        setItemData(prev => ({ ...prev, category: v }));
                                        if (validationErrors.category) {
                                            setValidationErrors(prev => ({ ...prev, category: null }));
                                        }
                                    }}
                                >
                                    <SelectTrigger className={`bg-white ${validationErrors.category ? 'border-pink-400' : ''}`} data-testid="item-category-select">
                                        <SelectValue placeholder="Select category" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white">
                                        {categories.map(cat => (
                                            <SelectItem key={cat} value={cat}>
                                                {t(cat)} — Starting bid €{STARTING_BIDS[cat]}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {validationErrors.category && (
                                    <p className="text-sm text-pink-500 mt-1">{validationErrors.category}</p>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium mb-2 block">{t('itemSize')}</label>
                                    <Input
                                        value={itemData.size_text}
                                        onChange={(e) => setItemData(prev => ({ ...prev, size_text: e.target.value }))}
                                        placeholder="e.g., M, 38, One size"
                                        className="input-minimal bg-white"
                                        data-testid="item-size-input"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium mb-2 block">{t('itemBrand')}</label>
                                    <Input
                                        value={itemData.brand}
                                        onChange={(e) => setItemData(prev => ({ ...prev, brand: e.target.value }))}
                                        placeholder="e.g., Zara"
                                        className="input-minimal bg-white"
                                        data-testid="item-brand-input"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-sm font-medium mb-2 block">{t('itemMaterials')}</label>
                                <Input
                                    value={itemData.materials_text}
                                    onChange={(e) => setItemData(prev => ({ ...prev, materials_text: e.target.value }))}
                                    placeholder="e.g., 80% Cotton, 20% Polyester"
                                    className="input-minimal bg-white"
                                    data-testid="item-materials-input"
                                />
                            </div>

                            <div>
                                <label className="text-sm font-medium mb-2 block">{t('itemCare')}</label>
                                <Textarea
                                    value={itemData.care_text}
                                    onChange={(e) => setItemData(prev => ({ ...prev, care_text: e.target.value }))}
                                    placeholder="e.g., Machine wash cold, tumble dry low"
                                    className="bg-white border-border focus:border-[#F8BBD6] resize-none"
                                    rows={2}
                                    data-testid="item-care-input"
                                />
                            </div>

                            <div>
                                <label className="text-sm font-medium mb-2 block">{t('itemStartingBid')}</label>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl font-mono text-muted-foreground">€</span>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={itemData.price}
                                        onChange={(e) => setItemData(prev => ({ ...prev, price: e.target.value }))}
                                        placeholder="0"
                                        className="pl-10 input-minimal text-2xl font-mono bg-white"
                                        data-testid="item-price-input"
                                    />
                                </div>
                                {itemData.category && (
                                    <p className="text-xs text-muted-foreground mt-2">
                                        Suggested starting bid for {t(itemData.category)}: €{STARTING_BIDS[itemData.category]}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Disclaimer */}
                        <div className={`bg-gray-50 border p-4 space-y-3 ${validationErrors.disclaimer ? 'border-pink-400' : 'border-gray-200'}`}>
                            <div className="flex items-start gap-3">
                                <Checkbox
                                    id="disclaimer"
                                    checked={disclaimerAccepted}
                                    onCheckedChange={(checked) => {
                                        setDisclaimerAccepted(checked);
                                        if (validationErrors.disclaimer) {
                                            setValidationErrors(prev => ({ ...prev, disclaimer: null }));
                                        }
                                    }}
                                    className="mt-0.5"
                                    data-testid="disclaimer-checkbox"
                                />
                                <label htmlFor="disclaimer" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
                                    {language === 'nl' 
                                        ? 'Ik begrijp dat als mijn item geen biedingen ontvangt, ik verantwoordelijk ben voor het meenemen na het event. Galeries Yess is niet verantwoordelijk voor onverkochte items.'
                                        : 'I understand that if my auction item receives no bids, I am responsible for taking it home after the event. Galeries Yess is not responsible for unsold items.'}
                                </label>
                            </div>
                            {validationErrors.disclaimer && (
                                <p className="text-sm text-pink-500">{validationErrors.disclaimer}</p>
                            )}
                        </div>

                        {/* Submit Buttons */}
                        <div className="space-y-3">
                            {/* Save Draft Button */}
                            <Button
                                type="button"
                                onClick={handleSaveDraft}
                                disabled={loading || !itemData.title || previewImages.length === 0}
                                variant="outline"
                                className="w-full h-11 rounded-full text-sm font-heading border-gray-300"
                                data-testid="save-draft-button"
                            >
                                {loading ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    language === 'nl' ? 'Concept opslaan' : 'Save Draft'
                                )}
                            </Button>
                            
                            {/* Publish Button */}
                            <Button
                                type="submit"
                                disabled={loading || !itemData.title || !itemData.category || previewImages.length === 0 || (!isEditMode && !canAddMore) || !disclaimerAccepted}
                                className="w-full h-14 rounded-full text-lg font-heading bg-[#F8BBD6] hover:bg-[#f0a8c8] text-foreground disabled:opacity-50"
                                data-testid="publish-item-button"
                            >
                                {loading ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <>
                                        <Check className="w-5 h-5 mr-2" />
                                        {language === 'nl' ? 'Publiceren' : 'Publish'}
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>
                )}
            </main>

            {/* Publish Confirmation Dialog */}
            <AlertDialog open={showPublishConfirm} onOpenChange={setShowPublishConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading">
                            {language === 'nl' ? 'Item publiceren?' : 'Publish item?'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {language === 'nl' 
                                ? 'Weet je zeker dat je dit item wilt publiceren? Het wordt zichtbaar in de galerie.'
                                : 'Are you sure you want to publish this item? It will be visible in the gallery.'}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>
                            {language === 'nl' ? 'Annuleren' : 'Cancel'}
                        </AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleConfirmPublish}
                            className="bg-[#F8BBD6] hover:bg-[#f0a8c8] text-black"
                        >
                            {language === 'nl' ? 'Ja, publiceren' : 'Yes, publish'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default AddItemPage;
